import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { AppShell } from '../../components/layout/AppShell';
import { PageLayout } from '../../components/common/PageLayout';
import { ContentCard } from '../../components/common/ContentCard';
import { EmptyState } from '../../components/common/EmptyState';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { PAGE_COLORS } from '../../utils/pageColors';
import type { GlassValue, GlassValueFormData, GlassValueWritePayload } from '../../utils/types';
import { 
  useGetGlassValuesQuery,
  useGetGlassValueQuery,
  useCreateGlassValueMutation,
  useUpdateGlassValueMutation,
  useDeleteGlassValueMutation,
} from '../../store/slices/apiSlice';
import { useToast } from '../../hooks/useToast';
import { Search, Filter, Plus, Edit2, Trash2, Settings, AlertTriangle, Shield } from 'lucide-react';

const PAGE_SIZE = 20;

const newDraftKey = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;

type PatternDraftRow = { key: string; label: string };

const schema = z.object({
  glass_type: z.string().min(1, 'Glass type is required'),
  is_arrival: z.boolean().default(false),
  is_cutting: z.boolean().default(false),
  applies_to_single: z.boolean().default(false),
  applies_to_double: z.boolean().default(false),
  applies_to_triple: z.boolean().default(false),
  applies_to_mirror: z.boolean().default(false),
  supports_patterned_options: z.boolean().default(false),
}).refine(v => v.applies_to_single || v.applies_to_double || v.applies_to_triple || v.applies_to_mirror, {
  message: 'Select at least one product type',
  path: ['applies_to_single']
}).refine(v => v.is_arrival || v.is_cutting, {
  message: 'Select at least one glass value (arrival or cutting)',
  path: ['is_arrival']
});

export const GlassValuesPage: React.FC = () => {
  const toast = useToast();
  const [editing, setEditing] = useState<GlassValue | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [patternDraft, setPatternDraft] = useState<PatternDraftRow[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [glassValueFilter, setGlassValueFilter] = useState('all');
  const [productTypeFilter, setProductTypeFilter] = useState('all');
  const [ordering, setOrdering] = useState('-created_at');

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm<GlassValueFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      glass_type: '',
      is_arrival: false,
      is_cutting: false,
      applies_to_single: false,
      applies_to_double: false,
      applies_to_triple: false,
      applies_to_mirror: false,
      supports_patterned_options: false,
    },
  });

  const supportsPatterned = watch('supports_patterned_options');

  const editingId = editing?.id;
  const { data: editingDetail, isFetching: editingDetailLoading } = useGetGlassValueQuery(editingId as string, {
    skip: !editingId,
  });

  useEffect(() => {
    if (!editing) return;
    if (!editingDetail || editingDetail.id !== editing.id) return;
    const opts = editingDetail.patterned_options;
    if (!opts?.length) {
      setPatternDraft([]);
      return;
    }
    setPatternDraft(
      [...opts]
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
        .map((o) => ({ key: o.id, label: o.label })),
    );
  }, [editing?.id, editingDetail, editing]);

  const [createGlassValue, { isLoading: creating }] = useCreateGlassValueMutation();
  const [updateGlassValue, { isLoading: updating }] = useUpdateGlassValueMutation();
  const [deleteGlassValue, { isLoading: deleting }] = useDeleteGlassValueMutation();

  const params = useMemo(() => ({
    page: currentPage,
    page_size: PAGE_SIZE,
    search: searchTerm || undefined,
    is_arrival: glassValueFilter === 'arrival' ? true : undefined,
    is_cutting: glassValueFilter === 'cutting' ? true : undefined,
    applies_to_single: productTypeFilter === 'single' ? true : undefined,
    applies_to_double: productTypeFilter === 'double' ? true : undefined,
    applies_to_triple: productTypeFilter === 'triple' ? true : undefined,
    applies_to_mirror: productTypeFilter === 'mirror' ? true : undefined,
    ordering,
  }), [currentPage, searchTerm, glassValueFilter, productTypeFilter, ordering]);

  const { data, isLoading, isError, error, refetch } = useGetGlassValuesQuery(params);

  const glassValues = data?.results ?? [];
  const totalCount = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setGlassValueFilter('all');
    setProductTypeFilter('all');
    setOrdering('-created_at');
    setCurrentPage(1);
  };

  const onSubmit = async (values: GlassValueFormData) => {
    if (editing && editingDetailLoading) {
      toast.info('Please wait', 'Loading patterned options…');
      return;
    }
    const patterned_options: GlassValueWritePayload['patterned_options'] = values.supports_patterned_options
      ? patternDraft.filter((r) => r.label.trim()).map((r) => ({ label: r.label.trim() }))
      : [];
    const payload: GlassValueWritePayload = { ...values, patterned_options };
    try {
      if (editing) {
        await updateGlassValue({ id: editing.id, data: payload }).unwrap();
        toast.success('Glass Value Updated', 'The glass value has been updated successfully.');
      } else {
        await createGlassValue(payload).unwrap();
        toast.success('Glass Value Created', 'The glass value has been created successfully.');
      }
      reset();
      setPatternDraft([]);
      setEditing(null);
      setShowCreateForm(false);
      refetch();
    } catch (e: any) {
      toast.error('Save Failed', e?.data?.detail || 'Please check the form and try again.');
    }
  };

  const onEdit = (item: GlassValue) => {
    setEditing(item);
    setShowCreateForm(true);
    setPatternDraft([]);
    reset({
      glass_type: item.glass_type,
      is_arrival: item.is_arrival,
      is_cutting: item.is_cutting,
      applies_to_single: item.applies_to_single,
      applies_to_double: item.applies_to_double,
      applies_to_triple: item.applies_to_triple,
      applies_to_mirror: item.applies_to_mirror,
      supports_patterned_options: !!item.supports_patterned_options,
    });
  };

  const onDelete = async (item: GlassValue) => {
    if (!confirm(`Delete "${item.glass_type}"?`)) return;
    try {
      await deleteGlassValue(item.id).unwrap();
      toast.success('Glass Value Deleted', 'The glass value has been deleted.');
      refetch();
    } catch (e: any) {
      toast.error('Delete Failed', e?.data?.detail || 'Please try again.');
    }
  };

  const formatDate = (iso: string) => {
    try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
  };

  const getProductTypes = (item: GlassValue) => {
    const types: string[] = [];
    if (item.applies_to_single) types.push('Single');
    if (item.applies_to_double) types.push('Double');
    if (item.applies_to_triple) types.push('Triple');
    if (item.applies_to_mirror) types.push('Mirror');
    return types.join(', ') || '—';
  };

  const getGlassValues = (item: GlassValue) => {
    const values: string[] = [];
    if (item.is_arrival) values.push('Arrival');
    if (item.is_cutting) values.push('Cutting');
    return values.join(', ') || '—';
  };

  const renderPagination = () => {
    if (!totalCount || totalCount <= PAGE_SIZE) return null;
    return (
      <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50">
        <div className="text-sm text-gray-700">
          Showing {(currentPage - 1) * PAGE_SIZE + 1} to {Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount} glass values
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
            Previous
          </Button>
          <span className="px-3 text-sm text-gray-600">Page {currentPage} of {totalPages}</span>
          <Button variant="ghost" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
            Next
          </Button>
        </div>
      </div>
    );
  };

  return (
    <AppShell>
      <PageLayout
        title="Glass Values Management"
        subtitle={`${totalCount} total glass values`}
        bgColor={PAGE_COLORS.settings.bgColor}
        textColor={PAGE_COLORS.settings.textColor}
        borderColor={PAGE_COLORS.settings.borderColor}
        icon={<Settings className="w-6 h-6" />}
      >
        {/* Create Form */}
        {showCreateForm && (
          <ContentCard
            title={editing ? 'Edit Glass Value' : 'Add New Glass Value'}
            subtitle={editing ? 'Update glass value details' : 'Create a new glass value specification'}
            actions={
              <Button
                variant="ghost"
                onClick={() => {
                  setShowCreateForm(false);
                  setEditing(null);
                  setPatternDraft([]);
                  reset({
                    glass_type: '',
                    is_arrival: false,
                    is_cutting: false,
                    applies_to_single: false,
                    applies_to_double: false,
                    applies_to_triple: false,
                    applies_to_mirror: false,
                    supports_patterned_options: false,
                  });
                }}
              >
                Cancel
              </Button>
            }
          >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Glass Type <span className="text-red-500">*</span>
                  </label>
                  <input 
                    className="form-input w-full" 
                    placeholder="e.g., 4mm Clear Toughened" 
                    {...register('glass_type')} 
                  />
                  {errors.glass_type && <p className="mt-1 text-sm text-red-600">{errors.glass_type.message}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Glass Value <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="inline-flex items-center">
                      <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" {...register('is_arrival')} />
                      <span className="ml-2 text-sm text-gray-700">Glass Arrival</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" {...register('is_cutting')} />
                      <span className="ml-2 text-sm text-gray-700">Glass Cutting</span>
                    </label>
                  </div>
                  {errors.is_arrival && <p className="mt-1 text-sm text-red-600">{errors.is_arrival.message as any}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Types <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="inline-flex items-center">
                      <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" {...register('applies_to_single')} />
                      <span className="ml-2 text-sm text-gray-700">Single Glazed</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" {...register('applies_to_double')} />
                      <span className="ml-2 text-sm text-gray-700">Double Glazed</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" {...register('applies_to_triple')} />
                      <span className="ml-2 text-sm text-gray-700">Triple Glazed</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" {...register('applies_to_mirror')} />
                      <span className="ml-2 text-sm text-gray-700">Mirror</span>
                    </label>
                  </div>
                  {errors.applies_to_single && <p className="mt-1 text-sm text-red-600">{errors.applies_to_single.message as any}</p>}
                </div>
              </div>

              <div className="md:col-span-2 border-t border-gray-200 pt-6">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    {...register('supports_patterned_options')}
                  />
                  <span className="text-sm font-medium text-gray-800">Supports patterned glass sub-options</span>
                </label>
                <p className="mt-1 text-xs text-gray-500">
                  When enabled, add pattern names (e.g. Cotswold). They appear in the order form dropdown in alphabetical order.
                </p>
                {supportsPatterned && (
                  <div className="mt-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Pattern options</span>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          setPatternDraft((rows) => [...rows, { key: newDraftKey(), label: '' }])
                        }
                      >
                        Add option
                      </Button>
                    </div>
                    {editing && editingDetailLoading && (
                      <p className="text-sm text-gray-500">Loading existing options…</p>
                    )}
                    {patternDraft.length === 0 && !editingDetailLoading ? (
                      <p className="text-sm text-gray-500">No options yet. Add at least one for the order dropdown to appear.</p>
                    ) : null}
                    <ul className="space-y-2">
                      {patternDraft.map((row) => (
                        <li key={row.key} className="flex flex-wrap items-end gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex-1 min-w-[160px]">
                            <label className="block text-xs text-gray-600 mb-1">Label</label>
                            <input
                              className="form-input w-full"
                              value={row.label}
                              onChange={(e) =>
                                setPatternDraft((rows) =>
                                  rows.map((r) => (r.key === row.key ? { ...r, label: e.target.value } : r)),
                                )
                              }
                              placeholder="e.g. Cotswold"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="mb-0.5"
                            onClick={() => setPatternDraft((rows) => rows.filter((r) => r.key !== row.key))}
                          >
                            Remove
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                <Button type="submit" disabled={creating || updating || (editing ? editingDetailLoading : false)}>
                  {creating || updating ? 'Saving...' : (editing ? 'Update Glass Value' : 'Create Glass Value')}
                </Button>
                <Button
                  variant="ghost"
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditing(null);
                    setPatternDraft([]);
                    reset({
                      glass_type: '',
                      is_arrival: false,
                      is_cutting: false,
                      applies_to_single: false,
                      applies_to_double: false,
                      applies_to_triple: false,
                      applies_to_mirror: false,
                      supports_patterned_options: false,
                    });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </ContentCard>
        )}

        {/* Main List */}
        <ContentCard
          title="All Glass Values"
          subtitle="Search, filter and manage glass values"
          actions={
            !showCreateForm ? (
              <Button
                icon={<Plus className="w-4 h-4" />}
                onClick={() => {
                  setEditing(null);
                  setPatternDraft([]);
                  reset({
                    glass_type: '',
                    is_arrival: false,
                    is_cutting: false,
                    applies_to_single: false,
                    applies_to_double: false,
                    applies_to_triple: false,
                    applies_to_mirror: false,
                    supports_patterned_options: false,
                  });
                  setShowCreateForm(true);
                }}
              >
                Add Glass Value
              </Button>
            ) : null
          }
        >
          {/* Search and Filters */}
          <div className="mb-6">
            <form onSubmit={handleSearchSubmit} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3 flex-1">
                <div className="relative flex-1 min-w-[240px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search glass types..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input pl-10"
                  />
                </div>

                <div className="hidden sm:flex items-center gap-2 text-gray-700">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium">Filters:</span>
                </div>

                <select
                  value={glassValueFilter}
                  onChange={(e) => { setGlassValueFilter(e.target.value); setCurrentPage(1); }}
                  className="form-input w-auto"
                >
                  <option value="all">All Values</option>
                  <option value="arrival">Arrival</option>
                  <option value="cutting">Cutting</option>
                </select>

                <select
                  value={productTypeFilter}
                  onChange={(e) => { setProductTypeFilter(e.target.value); setCurrentPage(1); }}
                  className="form-input w-auto"
                >
                  <option value="all">All Product Types</option>
                  <option value="single">Single Glazed</option>
                  <option value="double">Double Glazed</option>
                  <option value="triple">Triple Glazed</option>
                  <option value="mirror">Mirror</option>
                </select>

                <select
                  value={ordering}
                  onChange={(e) => { setOrdering(e.target.value); setCurrentPage(1); }}
                  className="form-input w-auto"
                >
                  <option value="-created_at">Newest First</option>
                  <option value="created_at">Oldest First</option>
                  <option value="glass_type">Glass Type (A-Z)</option>
                  <option value="-glass_type">Glass Type (Z-A)</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <Button type="submit" variant="secondary">Search</Button>
                {(searchTerm || glassValueFilter !== 'all' || productTypeFilter !== 'all' || ordering !== '-created_at') && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}>Clear</Button>
                )}
              </div>
            </form>
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          )}

          {/* Error */}
          {isError && (
            <div className={`alert mb-6 ${('status' in (error as any) && (error as any).status === 403) ? 'alert-warning' : 'alert-error'}`}>
              <div className="flex items-center">
                {('status' in (error as any) && (error as any).status === 403) ? (
                  <Shield className="w-5 h-5 mr-2" />
                ) : (
                  <AlertTriangle className="w-5 h-5 mr-2" />
                )}
                <span className="mr-2">
                  {('status' in (error as any) && (error as any).status === 403)
                    ? "You don't have permission to view glass values. Ask admin for 'glass_values_view' permission."
                    : 'Failed to load glass values. Please try again.'}
                </span>
                {!(('status' in (error as any) && (error as any).status === 403)) && (
                  <Button variant="ghost" size="sm" onClick={() => refetch()}>Retry</Button>
                )}
              </div>
            </div>
          )}

          {/* Empty */}
          {!isLoading && !isError && glassValues.length === 0 && (
            <EmptyState
              title="No glass values found"
              description={searchTerm ? 'Try adjusting search or filters.' : 'Create your first glass value to get started.'}
              action={
                <Button icon={<Plus className="w-4 h-4" />} onClick={() => setShowCreateForm(true)}>
                  Add Glass Value
                </Button>
              }
            />
          )}

          {/* Table */}
          {!isLoading && !isError && glassValues.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Glass Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Glass Value</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Patterned</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product Types</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {glassValues.map((item: GlassValue) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{item.glass_type}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {getGlassValues(item)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.supports_patterned_options ? 'Yes' : '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {getProductTypes(item)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(item.created_at)}
                          {item.created_by_username && (
                            <div className="text-xs text-gray-400">by {item.created_by_username}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <Button variant="ghost" size="sm" icon={<Edit2 className="w-4 h-4" />} onClick={() => onEdit(item)}>
                              Edit
                            </Button>
                            <Button variant="ghost" size="sm" icon={<Trash2 className="w-4 h-4" />} onClick={() => onDelete(item)} disabled={deleting}>
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {renderPagination()}
            </>
          )}
        </ContentCard>
      </PageLayout>
    </AppShell>
  );
};


